/**
 * FO-302 (batch-32, stream afwezigheid-klantmail) — generieke "klant informeren
 * bij afwezigheid"-contracten. Generalisatie van het FO-150-cursus-model naar
 * een afwezigheids-context (cursus / verlof / ziekmelding): één mail-model, één
 * dialoog, één verzend-/veroudering-mechanisme.
 *
 * De API parse't deze schema's server-side én het gedeelde dialoog valideert er
 * client-side mee (CLAUDE.md §Validatie & foutmeldingen — één bron).
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import type { WerkbonVerstuurContactDTO } from './werkbon-verzending'

// ─── Status + context ─────────────────────────────────────────────────────────

export const KLANT_AFWEZIGHEID_MAIL_STATUS = [
  'CONCEPT',
  'VERZONDEN',
  'VEROUDERD',
  'OVERGESLAGEN',
] as const
export type KlantAfwezigheidMailStatus = (typeof KLANT_AFWEZIGHEID_MAIL_STATUS)[number]

export const KLANT_AFWEZIGHEID_MAIL_STATUS_LABELS: Record<
  KlantAfwezigheidMailStatus,
  string
> = {
  CONCEPT: 'Concept bewaard',
  VERZONDEN: 'Klant geïnformeerd',
  VEROUDERD: 'Verouderd (afwezigheid gewijzigd)',
  OVERGESLAGEN: 'Overgeslagen',
}

export const KLANT_AFWEZIGHEID_CONTEXT_TYPE = [
  'OPLEIDING_SESSIE',
  'VERLOF_AANVRAAG',
  'ZIEKMELDING',
] as const
export type KlantAfwezigheidContextType =
  (typeof KLANT_AFWEZIGHEID_CONTEXT_TYPE)[number]

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén gecommuniceerde dag (yyyy-MM-dd + optionele HH:mm-tijden bij dagdeel). */
export interface KlantAfwezigheidMailDagDTO {
  datum: string
  startTijd: string | null
  eindTijd: string | null
}

export interface KlantAfwezigheidMailDTO {
  id: string
  contextType: KlantAfwezigheidContextType
  contextId: string
  clientId: string
  status: KlantAfwezigheidMailStatus
  /** Correctie-mail ("toch/weer beschikbaar") i.p.v. een gewone informeer-mail. */
  isCorrectie: boolean
  /** Ruwe (bewerkbare) tekst — placeholders nog niet ingevuld. */
  onderwerp: string
  body: string
  metVervangingsvraag: boolean
  employeeIds: string[]
  /** Snapshot van de gecommuniceerde dagen op moment van opstellen. */
  dagen: KlantAfwezigheidMailDagDTO[]
  /** Open-einde-verzuim: "vanaf {datum}, duur nog niet bekend". */
  openEinde: boolean
  horizonEinde: string | null
  ontvangers: string[]
  cc: string[]
  outgoingEmailId: string | null
  verzondenOp: string | null
  createdAt: string
}

export interface KlantAfwezigheidMedewerkerDTO {
  employeeId: string
  naam: string
}

export interface KlantAfwezigheidWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
}

/**
 * Eén kaart per geraakte klant: de gebundelde medewerker(s) en alléén de dagen
 * waarop die klant een planning-overlap met de afwezigheid heeft (FO-302 §6).
 * Live berekend uit afwezigheid × actuele planning — nooit gepersisteerd.
 */
export interface KlantAfwezigheidKaartDTO {
  clientId: string
  klantNaam: string
  medewerkers: KlantAfwezigheidMedewerkerDTO[]
  dagen: KlantAfwezigheidMailDagDTO[]
  werkbonnen: KlantAfwezigheidWerkbonDTO[]
  /** Open-einde-verzuim: de dagen beslaan de meldingshorizon. */
  openEinde: boolean
  horizonEinde: string | null
  /** Laatste (niet-correctie) informeer-mail voor deze (context, klant). */
  mail: KlantAfwezigheidMailDTO | null
  /** Laatste correctie-mail (na intrekking/afwijzing/vroege betermelding). */
  correctieMail: KlantAfwezigheidMailDTO | null
}

export interface KlantAfwezigheidOverlapDTO {
  contextType: KlantAfwezigheidContextType
  contextId: string
  kaarten: KlantAfwezigheidKaartDTO[]
}

// ─── Voorbeeld (preview = verzending) ────────────────────────────────────────

/**
 * Body van POST .../klant-mail/voorbeeld. Zonder onderwerp/body rendert de
 * server de standaard-template; mét = live preview van de bewerkte tekst.
 * `klantMailId` rendert een bewaard concept vanaf zijn eigen snapshot.
 * `correctie` schakelt naar het correctie-template ("toch/weer beschikbaar").
 */
export const KlantAfwezigheidMailVoorbeeldSchema = z.object({
  clientId: idParam('clientId'),
  klantMailId: idParam('klantMailId').optional(),
  correctie: z.boolean().optional(),
  onderwerp: z.string().trim().min(1).max(500).optional(),
  body: z.string().trim().min(1).max(20000).optional(),
  metVervangingsvraag: z.boolean().optional(),
})
export type KlantAfwezigheidMailVoorbeeldInput = z.infer<
  typeof KlantAfwezigheidMailVoorbeeldSchema
>

export interface KlantAfwezigheidMailVoorbeeldDTO {
  klantNaam: string
  /** Voorgestelde ontvangers uit de vaste-ontvanger-cascade (FO-112). */
  defaultTo: string[]
  defaultCc: string[]
  /** Contactpersonen mét e-mailadres, voor de ontvanger-picker. */
  contacten: WerkbonVerstuurContactDTO[]
  /** Leesbare context "Freek Twilt - hoofd TD" of `null` (lege-ontvanger-hint). */
  vasteOntvangerLabel: string | null
  /** E-mailadres van de ingelogde gebruiker (voor "Mijzelf in cc"). */
  eigenEmail: string | null
  /** Ruwe bewerkbare tekst (placeholders zichtbaar) voor de editor. */
  bewerkbaar: { onderwerp: string; body: string }
  /** Gerenderde preview via dezelfde pipeline als de verzending (FO-111). */
  voorbeeld: { onderwerp: string; bodyHtml: string }
  metVervangingsvraag: boolean
  /** De dagen + medewerkers die in de mail gecommuniceerd worden. */
  dagen: KlantAfwezigheidMailDagDTO[]
  medewerkers: KlantAfwezigheidMedewerkerDTO[]
  openEinde: boolean
  isCorrectie: boolean
}

// ─── Concept bewaren / versturen / overslaan ─────────────────────────────────

const emailAdres = z.string().trim().email('Ongeldig e-mailadres')

const klantMailTekstVelden = {
  onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(500),
  body: z.string().trim().min(1, 'Bericht is verplicht').max(20000),
  metVervangingsvraag: z.boolean(),
  cc: z.array(emailAdres).max(20).default([]),
}

/**
 * Body van POST .../klant-mail. Drie acties:
 *   - CONCEPT:   bewaren om later te versturen (ontvangers mogen nog leeg zijn);
 *   - VERZENDEN: direct versturen — minstens één geldige ontvanger verplicht;
 *   - OVERSLAAN: bewust niet mailen.
 * `klantMailId` richt de actie op een bestaand concept; `correctie` maakt/bewerkt
 * een correctie-mail (na intrekking/afwijzing/vroege betermelding).
 */
export const KlantAfwezigheidMailSchema = z.discriminatedUnion('actie', [
  z.object({
    actie: z.literal('CONCEPT'),
    clientId: idParam('clientId'),
    klantMailId: idParam('klantMailId').optional(),
    correctie: z.boolean().default(false),
    to: z.array(emailAdres).max(20).default([]),
    ...klantMailTekstVelden,
  }),
  z.object({
    actie: z.literal('VERZENDEN'),
    clientId: idParam('clientId'),
    klantMailId: idParam('klantMailId').optional(),
    correctie: z.boolean().default(false),
    to: z.array(emailAdres).min(1, 'Minstens één ontvanger is verplicht').max(20),
    maakOpvolgTaak: z.boolean().default(false),
    ...klantMailTekstVelden,
  }),
  z.object({
    actie: z.literal('OVERSLAAN'),
    clientId: idParam('clientId'),
  }),
])
export type KlantAfwezigheidMailInput = z.infer<typeof KlantAfwezigheidMailSchema>

/** Response van POST .../klant-mail. */
export interface KlantAfwezigheidMailResultDTO {
  klantMail: KlantAfwezigheidMailDTO
  /**
   * Alleen bij actie=VERZENDEN: `QUEUED` = verstuurd; `DROPPED_ALLOWLIST` = niet
   * verstuurd (allowlist/opt-out), de rij blijft CONCEPT zodat opnieuw versturen
   * mogelijk is.
   */
  verzendStatus?: 'QUEUED' | 'DROPPED_ALLOWLIST'
}

// ─── Planbord-suggestie (force over een afwezigheid) ─────────────────────────

/**
 * FO-302 §5.6 — na een geforceerde (om)planning over een bekende afwezigheid
 * geeft de planbord-response per geraakte afwezigheid deze pointer mee, zodat de
 * UI de informeer-kaart voor de nieuwe klant kan tonen. De kaart zelf wordt live
 * berekend (nooit gepersisteerd).
 */
export interface InformeerSuggestieDTO {
  contextType: KlantAfwezigheidContextType
  contextId: string
  clientId: string
}
